import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { memberApi } from '../../api';
import { UpdateMemberData, MemberData } from '../../api_types';
import { logger } from '../../shared/logger';


const MemberProfileSettings: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<MemberData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<UpdateMemberData>({});
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setError('No user found');
        setLoading(false);
        return;
      }

      if (!facilityId) {
        setError('No facility ID found');
        setLoading(false);
        return;
      }

      try {
        const data = await memberApi.getMemberData(user.uid, businessId, facilityId);
        setUserData(data);
        setEditData({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch user data');
        logger.error('Error fetching user data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user]);

  useEffect(() => {
    if (userData) {
      setEditData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone
      });
    }
  }, [userData]);

  const handleEdit = () => {
    setIsEditing(true);
    setUpdateError(null);
  };

  const handleSave = async () => {
    if (!user || !userData) return;
    
    if (!facilityId) {
      console.log('Debug: No facility ID found for user:', user.uid);
      setError('No facility ID found');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const updatedData = await memberApi.updateMemberData(user.uid, editData, businessId, facilityId);
      setUserData(updatedData);
      setIsEditing(false);
      setUpdateError(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update user data');
      logger.error('Error updating user data:', error);
      setUpdateError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UpdateMemberData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">No user data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Profile Information</h2>
        {!isEditing && (
          <button 
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Edit Profile
          </button>
        )}
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          Profile updated successfully!
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={userData.email}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded text-gray-500 bg-gray-50"
            />
            <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={isEditing ? (editData.phone || '') : (userData.phone || '')}
              onChange={handleInputChange('phone')}
              readOnly={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded ${!isEditing ? 'bg-gray-50' : ''}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={isEditing ? (editData.firstName || '') : userData.firstName}
              onChange={handleInputChange('firstName')}
              readOnly={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded ${!isEditing ? 'bg-gray-50' : ''}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={isEditing ? (editData.lastName || '') : userData.lastName}
              onChange={handleInputChange('lastName')}
              readOnly={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded ${!isEditing ? 'bg-gray-50' : ''}`}
            />
          </div>
        </div>

        {isEditing && (
          <div className="mt-6 flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default MemberProfileSettings; 